<template>
  <WizardModal
    :show-modal="visible"
    :modal-title="t('Aceitar Solicitação de Reembolso')"
    :steps="wizardSteps"
    size="compactDesktop"
    orientation="horizontal"
    :show-summary="true"
    :summary-items="summaryItems"
    :loading="isProcessing"
    @cancel="handleClose"
    @close="handleClose"
    @finish="handleFinish"
  >
    <!-- Step 0: Select Category -->
    <template #step-0>
      <div class="accept-step">
        <div class="refund-preview">
          <h4>{{ t('Detalhes do Reembolso') }}</h4>
          <div class="preview-row">
            <span class="preview-label">{{ t('Título') }}:</span>
            <span class="preview-value">{{ refund?.title }}</span>
          </div>
          <div class="preview-row">
            <span class="preview-label">{{ t('Valor') }}:</span>
            <span class="preview-value amount">{{ n(refund?.amount || 0, 'currency') }}</span>
          </div>
          <div class="preview-row">
            <span class="preview-label">{{ t('Data da Despesa') }}:</span>
            <span class="preview-value">{{ formatDate(refund?.expense_date) }}</span>
          </div>
          <div v-if="refund?.description" class="preview-row">
            <span class="preview-label">{{ t('Descrição') }}:</span>
            <span class="preview-value">{{ refund?.description }}</span>
          </div>
          <div v-if="refund?.file_url" class="preview-row">
            <span class="preview-label">{{ t('Comprovante') }}:</span>
            <a href="javascript:void(0)" class="receipt-link" data-testid="payroll-refunds-accept-view-receipt" @click.prevent="openReceiptGallery">
              <i class="pi pi-paperclip"></i>
              {{ t('Ver comprovante') }}
            </a>
          </div>
        </div>

        <div class="category-selection">
          <h4>{{ t('Selecione a Categoria da Despesa') }} *</h4>
          <p class="category-help">
            {{ t('Escolha a categoria para classificar esta despesa no sistema') }}
          </p>

          <div v-if="isLoadingCategories" class="loading-categories">
            <spinner style="width: 30px; height: 30px" />
            <span>{{ t('Carregando categorias...') }}</span>
          </div>

          <select-list
            v-else
            v-model="selectedCategoryId"
            elem-id="refund-accept-category"
            :label="''"
            :options="categories"
            option-label="despesa_categoria_titulo"
            option-value="despesa_categoria_id"
            :placeholder="t('Selecione uma categoria')"
            class="w-full category-dropdown"
            data-testid="payroll-refunds-accept-category-select"
            @update:modelValue="showValidation = false"
          />

          <small v-if="!selectedCategoryId && showValidation" class="p-error">
            {{ t('Categoria obrigatória') }}
          </small>
        </div>
      </div>
    </template>

    <!-- Step 1: Confirmation -->
    <template #step-1>
      <div class="confirm-step">
        <div class="confirm-icon">
          <i class="pi pi-check-circle"></i>
        </div>

        <h4>{{ t('Confirmar Aceitação') }}</h4>

        <div class="confirm-details">
          <p>{{ t('Você está prestes a aceitar o seguinte reembolso') }}:</p>

          <div class="confirm-card">
            <div class="confirm-row">
              <span class="confirm-label">{{ t('Título') }}:</span>
              <span class="confirm-value">{{ refund?.title }}</span>
            </div>
            <div class="confirm-row">
              <span class="confirm-label">{{ t('Valor') }}:</span>
              <span class="confirm-value amount">{{ n(refund?.amount || 0, 'currency') }}</span>
            </div>
            <div class="confirm-row">
              <span class="confirm-label">{{ t('Categoria') }}:</span>
              <span class="confirm-value">{{ selectedCategoryName }}</span>
            </div>
          </div>

          <div class="confirm-note">
            <i class="pi pi-info-circle"></i>
            <span>
              {{ t('Ao confirmar, uma despesa será criada automaticamente e vinculada a este reembolso.') }}
            </span>
          </div>
        </div>
      </div>
    </template>
  </WizardModal>

  <!-- Receipt Gallery -->
  <ImageGallery
    v-if="showGallery"
    :images="galleryImages"
    :initial-index="0"
    :loading="false"
    :empty-message="t('Nenhuma imagem encontrada')"
    :show-thumbnails="false"
    :show-zoom-controls="true"
    :show-fullscreen-control="true"
    :show-delete-button="false"
    @close="closeReceiptGallery"
  />
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { log } from '../../../utils/DebugLogger';
import { eventBus } from '../../../eventBus';
import { useDateFormatter } from '../../../composables/useDateFormatter';
import { usePayrollRefunds } from '../../../composables/usePayrollRefunds';
import { useDespesasCategorias } from '../../../composables/useDespesasCategorias';

type Id = number | string;

interface Refund {
  id?: Id;
  title?: string;
  amount?: number;
  expense_date?: string | Date;
  description?: string;
  file_url?: string;
  [key: string]: unknown;
}

interface Category {
  despesa_categoria_id: Id;
  despesa_categoria_titulo: string;
  despesa_categoria_status: number | string;
}

interface GalleryImage {
  id: Id;
  url: string;
  thumbnailUrl: string;
  label: string;
  type: string;
}

interface WizardStep {
  title: string;
  description: string;
  validate: () => boolean;
}

interface SummaryItem {
  label: string;
  value: string;
  icon: string;
}

interface Props {
  visible: boolean;
  refund?: Refund | null;
}

const props = withDefaults(defineProps<Props>(), {
  refund: null,
});

const emit = defineEmits<{
  'update:visible': [value: boolean];
  'completed': [result: unknown];
}>();

const { t, n } = useI18n();
const { formatDate: formatDateLocale } = useDateFormatter();
const { acceptRefund } = usePayrollRefunds();
const { fetchCategorias } = useDespesasCategorias();

const categories = ref<Category[]>([]);
const selectedCategoryId = ref<Id | null>(null);
const isLoadingCategories = ref<boolean>(false);
const isProcessing = ref<boolean>(false);
const showValidation = ref<boolean>(false);
const showGallery = ref<boolean>(false);
const galleryImages = ref<GalleryImage[]>([]);

const wizardSteps = computed((): WizardStep[] => [
  {
    title: t('Categoria'),
    description: t('Selecione a categoria'),
    validate: () => {
      if (!selectedCategoryId.value) {
        showValidation.value = true;
        return false;
      }
      return true;
    }
  },
  {
    title: t('Confirmação'),
    description: t('Confirme a aceitação'),
    validate: () => true
  }
]);

const summaryItems = computed((): SummaryItem[] => [
  { label: t('Título'), value: props.refund?.title || '-', icon: 'pi pi-file' },
  { label: t('Valor'), value: n(props.refund?.amount || 0, 'currency'), icon: 'pi pi-dollar' }
]);

const selectedCategoryName = computed((): string => {
  if (!selectedCategoryId.value) return '-';
  const category = categories.value.find(c => c.despesa_categoria_id === selectedCategoryId.value);
  return category?.despesa_categoria_titulo || '-';
});

const loadCategories = async (): Promise<void> => {
  log('PayrollRefundsAcceptWizard', 'loadCategories', 'Loading categories');
  isLoadingCategories.value = true;
  try {
    const data = await fetchCategorias();
    categories.value = (data || []).filter(
      (c: Category) => parseInt(String(c.despesa_categoria_status)) === 1
    );
    log('PayrollRefundsAcceptWizard', 'loadCategories', 'Categories loaded', { count: categories.value.length });
  } catch (err) {
    log('PayrollRefundsAcceptWizard', 'loadCategories', 'Error loading categories', err);
    eventBus.emit('globalMessage.error', { message: t('Erro ao carregar categorias'), duration: 5000 });
  } finally {
    isLoadingCategories.value = false;
  }
};

const formatDate = (date: string | Date | undefined): string => {
  if (!date) return '-';
  if (date instanceof Date) return formatDateLocale(date);
  return formatDateLocale(new Date(date));
};

const openReceiptGallery = (): void => {
  if (!props.refund?.file_url) return;
  log('PayrollRefundsAcceptWizard', 'openReceiptGallery', 'Opening receipt gallery', {
    fileUrl: props.refund.file_url,
    title: props.refund.title
  });
  galleryImages.value = [{
    id: props.refund.id || 'receipt',
    url: props.refund.file_url,
    thumbnailUrl: props.refund.file_url,
    label: props.refund.title || t('Comprovante'),
    type: 'receipt'
  }];
  showGallery.value = true;
};

const closeReceiptGallery = (): void => {
  log('PayrollRefundsAcceptWizard', 'closeReceiptGallery', 'Closing receipt gallery');
  showGallery.value = false;
  galleryImages.value = [];
};

const resetState = (): void => {
  selectedCategoryId.value = null;
  showValidation.value = false;
  isProcessing.value = false;
  showGallery.value = false;
  galleryImages.value = [];
};

const handleClose = (): void => {
  log('PayrollRefundsAcceptWizard', 'handleClose', 'Closing wizard');
  resetState();
  emit('update:visible', false);
};

const handleFinish = async (): Promise<void> => {
  log('PayrollRefundsAcceptWizard', 'handleFinish', 'Finishing wizard', {
    refundId: props.refund?.id,
    categoryId: selectedCategoryId.value
  });
  if (!props.refund?.id || !selectedCategoryId.value) {
    log('PayrollRefundsAcceptWizard', 'handleFinish', 'Missing required data');
    return;
  }
  isProcessing.value = true;
  try {
    const result = await acceptRefund(props.refund.id, Number(selectedCategoryId.value));
    if (result) {
      log('PayrollRefundsAcceptWizard', 'handleFinish', 'Refund accepted', result);
      eventBus.emit('globalMessage.success', { message: t('Reembolso aceito com sucesso'), duration: 3000 });
      emit('completed', result);
      handleClose();
    } else {
      eventBus.emit('globalMessage.error', { message: t('Erro ao aceitar reembolso'), duration: 5000 });
    }
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    log('PayrollRefundsAcceptWizard', 'handleFinish', 'Error accepting refund', err);
    eventBus.emit('globalMessage.error', { message: message || t('Erro ao aceitar reembolso'), duration: 5000 });
  } finally {
    isProcessing.value = false;
  }
};

watch(() => props.visible, (newVal) => {
  if (newVal) {
    loadCategories();
    resetState();
  }
});

onMounted(() => {
  if (props.visible) loadCategories();
});
</script>

<style scoped>
.accept-step {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.refund-preview {
  background: var(--surface-50);
  border: 1px solid var(--surface-200);
  border-radius: 8px;
  padding: 1rem;
}

.refund-preview h4 {
  margin: 0 0 1rem 0;
  font-size: 1rem;
  color: var(--text-color);
}

.preview-row {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
}

.preview-row:last-child {
  margin-bottom: 0;
}

.preview-label {
  font-weight: 600;
  color: var(--text-color-secondary);
  min-width: 120px;
}

.preview-value {
  color: var(--text-color);
}

.preview-value.amount {
  font-weight: 600;
  color: var(--primary-600);
}

.receipt-link {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  color: var(--primary-color);
  text-decoration: none;
}

.receipt-link:hover {
  text-decoration: underline;
}

.category-selection {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.category-selection h4 {
  margin: 0;
  font-size: 1rem;
  color: var(--text-color);
}

.category-help {
  margin: 0;
  font-size: 0.875rem;
  color: var(--text-color-secondary);
}

.loading-categories {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  color: var(--text-color-secondary);
}

.category-dropdown {
  margin-top: 0.5rem;
}

.w-full {
  width: 100%;
}

.p-error {
  color: var(--red-500);
  font-size: 0.875rem;
}

/* Confirmation Step */
.confirm-step {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 1rem;
  padding: 1rem;
}

.confirm-icon {
  font-size: 3rem;
  color: var(--green-500);
}

.confirm-step h4 {
  margin: 0;
  font-size: 1.25rem;
  color: var(--text-color);
}

.confirm-details {
  width: 100%;
  max-width: 400px;
  text-align: left;
}

.confirm-details p {
  text-align: center;
  color: var(--text-color-secondary);
  margin-bottom: 1rem;
}

.confirm-card {
  background: var(--surface-50);
  border: 2px solid var(--green-200);
  border-radius: 8px;
  padding: 1rem;
}

.confirm-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
  border-bottom: 1px solid var(--surface-200);
}

.confirm-row:last-child {
  border-bottom: none;
}

.confirm-label {
  font-weight: 600;
  color: var(--text-color-secondary);
}

.confirm-value {
  color: var(--text-color);
}

.confirm-value.amount {
  font-weight: 700;
  color: var(--green-600);
  font-size: 1.125rem;
}

.confirm-note {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  margin-top: 1rem;
  padding: 0.75rem;
  background: var(--blue-50);
  border-radius: 6px;
  font-size: 0.875rem;
  color: var(--blue-700);
}

.confirm-note i {
  flex-shrink: 0;
  margin-top: 0.125rem;
}

@media (max-width: 768px) {
  .preview-row {
    flex-direction: column;
    gap: 0.25rem;
  }

  .preview-label {
    min-width: auto;
  }

  .confirm-details {
    max-width: 100%;
  }
}
</style>
